import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { GlobalAccount, GUID, HouseholdType, PersonOrgProfile } from '@ndvr/customer-api';

import { FormSheetContent } from '@/components/FormSheet';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { FinancialPicture, PersonBasicFormFields, PrimaryAdvisorSelector, usePersonSelector } from 'modules';
import { getGlobalAccountRoleDisplayLabel } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { usePersonCoreDataForTenant } from 'views/person/person-queries';
import { PotentialDuplicateDialog } from 'views/person/PotentialDuplicateDialog';
import { checkPersonDuplicates, usePotentialPersonDuplicates } from 'views/person/usePotentialPersonDuplicates';
import { Button, FormSection, HorizontalLayout, HouseholdNameLink, Select, Text, Title, VerticalLayout } from 'widgets';

import { useCreateGlobalAccount, usePersonOrgGlobalAccountRoles } from './household-queries';

interface Props {
    personProfile?: PersonOrgProfile;
    onClose: () => void;
    onSuccess: (guid: GUID<GlobalAccount>) => void;
    isProspectRoute?: boolean;
    defaultPerson?: { firstName?: string; lastName?: string };
}

export const HouseholdDrawer = ({ onClose, onSuccess, isProspectRoute = false, defaultPerson }: Props) => {
    const { user, selectedTenants } = useAuthContext();
    const { mutate: createGlobalAccount, isPending } = useCreateGlobalAccount();
    const [createNew, setCreateNew] = useState(true);
    const automaticTenantGUID = selectedTenants.length === 1 ? selectedTenants[0].tenantGUID : '';
    const selectedTenantScopeKey = selectedTenants.map(({ tenantGUID }) => tenantGUID).join('|');
    const [selectedTenantGUID, setSelectedTenantGUID] = useState(automaticTenantGUID);

    const { data: appUsers, isLoading: isLoadingAppUsers } = usePersonCoreDataForTenant(
        createNew ? undefined : selectedTenantGUID,
    );
    const { PersonSelector, selectedPersonProfile, reset } = usePersonSelector({
        profiles: appUsers,
        isLoading: isLoadingAppUsers,
    });

    const { data: roles } = usePersonOrgGlobalAccountRoles(selectedPersonProfile?.personOrgGUID);

    useLayoutEffect(() => {
        setSelectedTenantGUID(automaticTenantGUID);
    }, [automaticTenantGUID, selectedTenantScopeKey]);

    useLayoutEffect(() => {
        reset();
    }, [reset, selectedTenantGUID]);

    const duplicates = usePotentialPersonDuplicates(
        JSON.stringify([
            selectedTenantGUID,
            selectedTenantScopeKey,
            createNew,
            selectedPersonProfile?.personOrgGUID,
            isProspectRoute,
        ]),
    );

    const { check: checkDuplicates } = duplicates;

    const submitCallback = useCallback(
        ({ primaryAdvisorGUID, person, financialPicture }: any) => {
            if (!selectedTenantGUID || (!createNew && !selectedPersonProfile)) {
                return;
            }

            const payload = createNew
                ? {
                      primaryAdvisorGUID,
                      person: financialPicture ? { ...person, ...financialPicture } : person,
                      tenantGUID: selectedTenantGUID,
                      householdType: isProspectRoute ? HouseholdType.PROSPECT : undefined,
                  }
                : {
                      primaryAdvisorGUID,
                      personOrgGUID: selectedPersonProfile.personOrgGUID,
                      tenantGUID: selectedTenantGUID,
                      householdType: isProspectRoute ? HouseholdType.PROSPECT : undefined,
                  };
            const create = () =>
                createGlobalAccount(payload, {
                    onSuccess: (globalAccount) => {
                        if (globalAccount) onSuccess(globalAccount.globalAccountGUID);
                    },
                });
            if (createNew) {
                void checkDuplicates(() => checkPersonDuplicates(selectedTenantGUID, person), create);
            } else {
                create();
            }
        },
        [
            createNew,
            selectedPersonProfile,
            selectedTenantGUID,
            isProspectRoute,
            createGlobalAccount,
            onSuccess,
            checkDuplicates,
        ],
    );

    const accountFormDefaultValues = useMemo(
        () => ({
            primaryAdvisorGUID: user?.personOrgGUID,
            ...(defaultPerson
                ? { person: { firstName: defaultPerson.firstName, lastName: defaultPerson.lastName } }
                : {}),
        }),
        [user?.personOrgGUID, defaultPerson],
    );

    return (
        <>
            <Sheet
                defaultOpen
                onExitComplete={onClose}
                onOpenChange={(open) => {
                    if (!open) duplicates.cancel();
                }}
            >
                <SheetContent layout="scroll-body" size="md" aria-describedby={undefined}>
                    <FormSheetContent
                        title={isProspectRoute ? 'Create New Prospect' : 'Create New Household'}
                        onSubmit={submitCallback}
                        defaultValues={accountFormDefaultValues}
                        pending={isPending || duplicates.busy}
                        submitDisabled={!selectedTenantGUID}
                        submitLabel="Create"
                    >
                        <fieldset disabled={isPending || duplicates.busy} className="contents">
                            {selectedTenants.length !== 1 && (
                                <FormSection title="Client Base">
                                    <Select
                                        aria-label="Client Base"
                                        value={selectedTenantGUID}
                                        onChange={(event) => setSelectedTenantGUID(event.target.value)}
                                    >
                                        <option value="">Select Client Base</option>
                                        {selectedTenants.map((tenant) => (
                                            <option key={tenant.tenantGUID} value={tenant.tenantGUID}>
                                                {tenant.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormSection>
                            )}

                            <FormSection title="Advisor">
                                <PrimaryAdvisorSelector
                                    tenantGUID={selectedTenantGUID || undefined}
                                    defaultAdvisorGUID={user?.personOrgGUID}
                                />
                            </FormSection>

                            <FormSection hasDivider>
                                <VerticalLayout $gap={8}>
                                    <HorizontalLayout $rowCenter $apart>
                                        <Title content>{createNew ? 'Create' : 'Select'} Principal</Title>
                                        {createNew ? (
                                            <Button buttonType="ghost" onClick={() => setCreateNew(false)}>
                                                Use Existing Person
                                            </Button>
                                        ) : (
                                            <Button
                                                buttonType="ghost"
                                                onClick={() => {
                                                    setCreateNew(true);
                                                    reset();
                                                }}
                                            >
                                                Create New Person
                                            </Button>
                                        )}
                                    </HorizontalLayout>
                                </VerticalLayout>
                            </FormSection>

                            {createNew ? (
                                <VerticalLayout $gap={24}>
                                    <PersonBasicFormFields prefix="person" />
                                    {isProspectRoute && (
                                        <FormSection title="Financial Picture">
                                            <FinancialPicture prefix="financialPicture" />
                                        </FormSection>
                                    )}
                                </VerticalLayout>
                            ) : (
                                <FormSection>
                                    {selectedTenantGUID ? (
                                        <PersonSelector />
                                    ) : (
                                        <Text helper $themeColor="subtle">
                                            Select a tenant before selecting an existing person.
                                        </Text>
                                    )}
                                    {roles && roles?.length > 0 && (
                                        <VerticalLayout $gap={4}>
                                            Selected person&apos;s existing roles
                                            <VerticalLayout>
                                                {roles.map((role) => (
                                                    <HorizontalLayout
                                                        key={role.globalAccountPersonOrgRoleGUID}
                                                        $gap={8}
                                                    >
                                                        <HouseholdNameLink householdGUID={role.globalAccountGUID} />
                                                        <Text>{getGlobalAccountRoleDisplayLabel(role)}</Text>
                                                    </HorizontalLayout>
                                                ))}
                                            </VerticalLayout>
                                        </VerticalLayout>
                                    )}
                                </FormSection>
                            )}
                        </fieldset>
                    </FormSheetContent>
                </SheetContent>
            </Sheet>
            <PotentialDuplicateDialog
                result={duplicates.result}
                onBack={duplicates.cancel}
                onProceed={duplicates.proceed}
            />
        </>
    );
};
